import React, { useEffect, useState } from "react";

import styled from "styled-components";

import Button from "components/atoms/Button";
import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Checkbox from "components/atoms/input/Checkbox";
import FormInput from "components/atoms/input/FormInput";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useSpace, useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { getExitSurvey } from "utils/request/auth";
import { apiUrl } from "utils/urls";

import axios from "axios";

const ContentsWrap = styled.div`
    padding: 40px 0 160px 0;
    width: 100%;
    max-width: 1080px;
`;

const Section = styled.div`
    &:not(:first-of-type) {
        margin-top: 40px;
    }
`;

const SectionTitle = styled(Text).attrs({
    withBarType: true,
    variant: "body-md-500",
    style: { display: "flex", alignItems: "center", gap: "8px" }, // 비밀번호 변경 버튼 위치에 사용되는 gap 설정
})``;

const SectionContents = styled.div`
    padding: 20px 10px 0 10px;
`;

const SectionContentsText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const FoldViewWrap = styled.div`
    display: flex;
    width: 100%;
    justify-content: flex-start;
`;

const FoldView = styled.div`
    display: ${(props) => (props.isVisible ? "block" : "none")};
    width: 100%;
    max-width: 480px;
`;

const SubCategory = styled.div`
    display: flex;
    align-items: center;
    margin: ${(props) => props.margin || "0 0 20px 0"};
    min-height: 32px;
    gap: 16px;
`;

const SubTitleWrap = styled.div`
    display: flex;
    width: 120px;
    flex-shrink: 0;
`;

const SubTitleText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})``;

const Row = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    width: 100%;
    gap: 4px;
`;

const ButtonSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    gap: 4px;
    margin-top: 16px;
`;

const DivideLine = styled.div`
    margin: 40px 0;
    width: 100%;
    height: 1px;
    background-color: var(--color-Line);
`;

const SurveyWrap = styled.div`
    padding: 40px 0 160px 0;
    max-width: 680px;
`;

const SurveyTitle = styled(Text).attrs({
    variant: "title-sm-500",
})`
    margin-bottom: 36px;
`;

const SurveyDescription = styled(Text).attrs({
    variant: "body-md-400",
})`
    margin-bottom: 8px;
`;

const SurveyNote = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    margin-top: 18px;
`;

const SurveyDivider = styled.div`
    height: 1px;
    background-color: var(--color-Line);
    margin: 32px 0;
`;

const SurveyCheckboxWrap = styled.div`
    display: flex;
    flex-direction: column;
`;

const SurveyCheckboxRow = styled.div`
    display: flex;
    align-items: center;
    margin: 6px 0;
`;

const SurveyQuestionText = styled(Text).attrs({
    variant: "body-md-500",
})`
    margin-bottom: 24px;
    ${(props) => props.depth === 1 && "font-weight: 400; font-size: 1.4rem; margin-bottom: 12px;"};
`;

const SurveyAnswerText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})`
    margin-left: 12px;
`;

const SurveyFoldView = styled.div`
    margin: 4px 0 4px 32px;
    padding: 16px;
    background-color: var(--color-BaseBlue);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    ${(props) =>
        props.depth === 1 &&
        `
        margin-left: 4px;
        padding: 0;
    `}
`;

const SurveyDetailTextarea = styled.textarea`
    width: 100%;
    min-height: 60px;
    padding: 12px 14px;
    font-size: 1.4rem;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    border: 1px solid var(--color-Line);
    border-radius: 4px;
    line-height: 1.5;
    resize: none;
    box-sizing: border-box;

    &::placeholder {
        color: var(--color-Grey1);
    }

    &:focus {
        outline: none;
        border-color: var(--color-Outline);
    }
`;

// --- Component ---
function AccountPage() {
    const { setToast } = useToast();
    const { setLoading } = useLoading();

    const [showUnregisterSurvey, setShowUnregisterSurvey] = useState(false);

    const [user, setUser] = useState({});
    const userQuery = useUser();

    const [spaces, setSpaces] = useState([]);
    const spaceQuery = useSpace();

    useEffect(() => {
        userQuery.refetch();
        spaceQuery.refetch();
    }, []);

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    useEffect(() => {
        if (spaceQuery.data) {
            setSpaces(spaceQuery.data);
        }
    }, [spaceQuery.data]);

    //region: 마케팅 수신 동의 처리
    const changeMarketingAgreedApi = (is_marketing_agreed) => {
        axios
            .post(apiUrl("changeMarketing"), {
                is_marketing_agreed: is_marketing_agreed,
            })
            .then((res) => {
                if (res.status === 204) {
                    userQuery.refetch();
                }
            })
            .catch((e) => {
                if (e.response.status === 400) {
                    Alert("warn", "에러", e.response.data.error);
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
                }
            });
    };

    //region: 탈퇴 처리
    const unregisterApi = () => {
        const paid_spaces = spaces.filter(
            (s) =>
                s.my_member_info.role === Constants.SPACE_MEMBER_ROLE_ADMIN &&
                s.status === Constants.SPACE_STATUS_ACTIVE &&
                s.plan.id !== Constants.FREE_PLAN,
        );
        if (paid_spaces.length === 0) {
            Confirm("warn", "탈퇴", "탈퇴하겠습니까? 탈퇴시 모든 데이터는 삭제됩니다.", "탈퇴", (e) => {
                callUnregisterApi();
            });
        } else {
            Confirm(
                "warn",
                "탈퇴",
                "유료 요금제를 이용 중입니다. 남은 기간은 환불되지 않으며 모든 데이터가 삭제됩니다. 탈퇴하겠습니까?",
                "탈퇴",
                (e) => {
                    callUnregisterApi();
                },
            );
        }
    };

    const callUnregisterApi = () => {
        setLoading(true);
        const options = checkecOptions.filter((o) => o.answer_text || (o.answers && o.answers.length > 0));

        axios
            .post(apiUrl("unregister"), {
                survey: options,
            })
            .then((res) => {
                setLoading(false);
                if (res.status === 204) {
                    Alert("info", "탈퇴", "탈퇴되었습니다.");
                    localStorage.removeItem("token");
                    userQuery.remove();
                    window.location.href = "/";
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
                }
            })
            .catch((e) => {
                setLoading(false);
                if (e.response.status === 400) {
                    Alert("warn", "에러", e.response.data.error);
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
                }
            });
    };

    // region: 비밀번호 변경 처리
    const [isPasswordVisible, setIsPasswordVisible] = useState(true);
    const [password, setPassword] = useState("");
    const [passwordChecked, setPasswordChecked] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
    const [passwordValidationError, setPasswordValidationError] = useState({});

    useEffect(() => {
        setPasswordChecked(false);
    }, [password]);

    const prevPasswordCheck = () => {
        setLoading(true, "비밀번호를 확인 중입니다");
        axios
            .post(apiUrl("changePassword"), { password: password }, { params: { commit: "check" } })
            .then((res) => {
                setLoading(false);
                if (res.status === 200) {
                    Alert("info", "확인", "비밀번호를 확인했습니다.");
                    setPasswordChecked(true);
                }
            })
            .catch((e) => {
                setLoading(false);
                if (e.response.status === 400) {
                    Alert("warn", "에러", e.response.data.error);
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
                }
            });
    };

    const changePasswordApi = () => {
        setLoading(true, "비밀번호를 변경 중입니다");
        axios
            .post(apiUrl("changePassword"), {
                password: password,
                new_password: newPassword,
                new_password_confirm: newPasswordConfirm,
            })
            .then((res) => {
                setLoading(false);
                if (res.status === 204) {
                    Alert("info", "변경", "비밀번호가 변경되었습니다.");
                    setPasswordChecked(false);
                    setPassword("");
                    setNewPassword("");
                    setNewPasswordConfirm("");
                    setIsPasswordVisible(false);
                }
            })
            .catch((e) => {
                setLoading(false);
                if (e.response.status === 400) {
                    Alert("warn", "에러", e.response.data.error);
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
                }
            });
    };

    const validationHandler = (e) => {
        if (e.target.name === "newPassword") {
            setNewPassword(e.target.value);
            let enPattern = /[a-zA-Z]/;
            let numPattern = /[0-9]/;
            if (!e.target.value) {
                setPasswordValidationError({ ...passwordValidationError, newPassword: "비밀번호를 입력해주세요." });
                return;
            }
            if (e.target.value.length < 6) {
                setPasswordValidationError({
                    ...passwordValidationError,
                    newPassword: "비밀번호는 6자 이상이어야 합니다.",
                });
                return;
            }
            if (!enPattern.test(e.target.value) || !numPattern.test(e.target.value)) {
                setPasswordValidationError({
                    ...passwordValidationError,
                    newPassword: "비밀번호는 영문과 숫자를 포함해야 합니다.",
                });
                return;
            }
            if (e.target.value !== newPasswordConfirm && newPasswordConfirm) {
                setPasswordValidationError({
                    ...passwordValidationError,
                    newPasswordConfirm: "비밀번호가 일치하지 않습니다.",
                    newPassword: undefined,
                });
                return;
            }
            setPasswordValidationError({
                ...passwordValidationError,
                newPassword: undefined,
                newPasswordConfirm: undefined,
            });
        } else if (e.target.name === "newPasswordConfirm") {
            setNewPasswordConfirm(e.target.value);
            if (e.target.value !== newPassword) {
                setPasswordValidationError({
                    ...passwordValidationError,
                    newPasswordConfirm: "비밀번호가 일치하지 않습니다.",
                });
                return;
            } else {
                setPasswordValidationError({ ...passwordValidationError, newPasswordConfirm: undefined });
            }
        }
        return true;
    };

    // region: 회원탈퇴 설문
    const [checkecOptions, setCheckedOptions] = useState([]);
    const [questions, setQuestions] = useState([]);

    useEffect(() => {
        getExitSurvey()
            .then((res) => {
                setQuestions(res.questions);
            })
            .catch((e) => {
                Alert("warn", "에러", "서버에 문제가 있습니다. 잠시후 시도해주세요.");
            });
    }, []);

    const renderSurveyQuestions = (questions) => {
        return questions.map((question) => {
            const depth = question.parent_option !== null ? 1 : 0;
            return (
                <div key={question.id}>
                    {depth === 0 && <SurveyDivider />}
                    {question.question_text && (
                        <SurveyQuestionText depth={depth}>{question.question_text}</SurveyQuestionText>
                    )}
                    {question.question_type === "subjective" ? (
                        <SurveyDetailTextarea
                            placeholder="입력해주세요"
                            multiline
                            rows={4}
                            value={checkecOptions.find((o) => o.question_id === question.id)?.answer_text || ""}
                            onChange={(e) => {
                                const newAnswer = e.target.value;
                                setCheckedOptions((prev) => {
                                    const existingAnswer = prev.find((o) => o.question_id === question.id);
                                    if (existingAnswer) {
                                        return prev.map((o) =>
                                            o.question_id === question.id ? { ...o, answer_text: newAnswer } : o,
                                        );
                                    } else {
                                        return [...prev, { question_id: question.id, answer_text: newAnswer }];
                                    }
                                });
                            }}
                        />
                    ) : (
                        <SurveyCheckboxWrap>
                            {question.options.map((option) => {
                                const isChecked = checkecOptions.some(
                                    (o) => o.question_id === question.id && o.answers?.includes(option.id),
                                );
                                return (
                                    <React.Fragment key={option.id}>
                                        <SurveyCheckboxRow>
                                            <Checkbox
                                                checked={isChecked}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setCheckedOptions((prev) => {
                                                        const existingAnswer = prev.find(
                                                            (o) => o.question_id === question.id,
                                                        );
                                                        if (existingAnswer) {
                                                            if (isChecked) {
                                                                return prev.map((o) =>
                                                                    o.question_id === question.id
                                                                        ? {
                                                                              ...o,
                                                                              answers: [
                                                                                  ...(o.answers || []),
                                                                                  option.id,
                                                                              ],
                                                                          }
                                                                        : o,
                                                                );
                                                            } else {
                                                                return prev.map((o) =>
                                                                    o.question_id === question.id
                                                                        ? {
                                                                              ...o,
                                                                              answers: (o.answers || []).filter(
                                                                                  (ans) => ans !== option.id,
                                                                              ),
                                                                          }
                                                                        : o,
                                                                );
                                                            }
                                                        } else {
                                                            return [
                                                                ...prev,
                                                                {
                                                                    question_id: question.id,
                                                                    answers: isChecked ? [option.id] : [],
                                                                },
                                                            ];
                                                        }
                                                    });
                                                }}
                                            />
                                            <SurveyAnswerText>{option.option_text}</SurveyAnswerText>
                                        </SurveyCheckboxRow>
                                        {option.questions?.length > 0 &&
                                            checkecOptions.some(
                                                (o) => o.question_id === question.id && o.answers?.includes(option.id),
                                            ) && (
                                                <SurveyFoldView depth={depth}>
                                                    {renderSurveyQuestions(option.questions)}
                                                </SurveyFoldView>
                                            )}
                                    </React.Fragment>
                                );
                            })}
                        </SurveyCheckboxWrap>
                    )}
                </div>
            );
        });
    };

    //region: 마크업 시작
    return (
        <LoginUserLayout>
            <HtmlHead title={"계정정보"} />
            {!showUnregisterSurvey ? (
                <>
                    <Breadcrumb>
                        <BreadcrumbTitle>계정정보</BreadcrumbTitle>
                    </Breadcrumb>
                    <BodyWithBreadcrumb>
                        <ContentsWrap>
                            {/* Email Section */}
                            <Section>
                                <SectionTitle>이메일 주소</SectionTitle>
                                <SectionContents>
                                    <SectionContentsText>{user?.email}</SectionContentsText>
                                </SectionContents>
                            </Section>

                            {/* Password Change Section */}
                            <Section>
                                <SectionTitle>
                                    비밀번호 변경
                                    <Button
                                        onlyIcon
                                        buttonSize={"auto"}
                                        onClick={(e) => setIsPasswordVisible(!isPasswordVisible)}
                                        bgColor={"var(--color-White)"}
                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                        border={isPasswordVisible ? "solid 1px var(--color-Button2)" : "none"}
                                    >
                                        <Icon
                                            name={isPasswordVisible ? "arrowUp" : "arrowDown"}
                                            color={"var(--color-SubBlack)"}
                                            size={"10"}
                                        />
                                    </Button>
                                </SectionTitle>
                                <SectionContents>
                                    <FoldViewWrap>
                                        <FoldView isVisible={isPasswordVisible}>
                                            <form>
                                                {/* 콘솔 에러 제거: password 는 form 안에 있어야함 */}
                                                <SubCategory>
                                                    <SubTitleWrap>
                                                        <SubTitleText>기존 비밀번호</SubTitleText>
                                                    </SubTitleWrap>
                                                    <Row>
                                                        <FormInput
                                                            labelDp="none"
                                                            height={32}
                                                            placeholder="본인 확인용 비밀번호 입력"
                                                            type="password"
                                                            value={password}
                                                            onChange={(e) => setPassword(e.target.value)}
                                                            disabled={passwordChecked}
                                                        />
                                                        <Button
                                                            onlyIcon
                                                            buttonSize={"32px"}
                                                            bgColor={"var(--color-Button1)"}
                                                            hoverBgColor={"var(--color-ButtonHover1)"}
                                                            onClick={prevPasswordCheck}
                                                            disabled={passwordChecked}
                                                        >
                                                            <Icon
                                                                name={"check"}
                                                                size={"12"}
                                                                color={"var(--color-White)"}
                                                            />
                                                        </Button>
                                                    </Row>
                                                </SubCategory>

                                                <SubCategory>
                                                    <SubTitleWrap>
                                                        <SubTitleText>새 비밀번호</SubTitleText>
                                                    </SubTitleWrap>
                                                    <Row>
                                                        <FormInput
                                                            labelDp="none"
                                                            height={32}
                                                            placeholder="영문, 숫자 포함 6자 이상"
                                                            type="password"
                                                            disabled={!passwordChecked}
                                                            name={"newPassword"}
                                                            errorMessage={passwordValidationError?.newPassword}
                                                            value={newPassword}
                                                            onChange={validationHandler}
                                                        />
                                                    </Row>
                                                </SubCategory>

                                                <SubCategory margin="0 0 32px 0">
                                                    <SubTitleWrap>
                                                        <SubTitleText>새 비밀번호 확인</SubTitleText>
                                                    </SubTitleWrap>
                                                    <Row>
                                                        <FormInput
                                                            labelDp="none"
                                                            height={32}
                                                            placeholder="새 비밀번호 재입력"
                                                            type="password"
                                                            disabled={!passwordChecked}
                                                            name={"newPasswordConfirm"}
                                                            errorMessage={passwordValidationError?.newPasswordConfirm}
                                                            value={newPasswordConfirm}
                                                            onChange={validationHandler}
                                                        />
                                                    </Row>
                                                </SubCategory>

                                                <ButtonSection>
                                                    <Button
                                                        onlyText
                                                        width={"80px"}
                                                        height={"32px"}
                                                        fontColor={"var(--color-Grey1)"}
                                                        buttonText={"취소"}
                                                        onClick={(e) => setIsPasswordVisible(false)}
                                                        bgColor="transparent"
                                                        hoverBgColor="var(--color-Background)"
                                                    />
                                                    <Button
                                                        onlyText
                                                        width={"80px"}
                                                        height={"32px"}
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        fontColor={"var(--color-White)"}
                                                        buttonText={"변경"}
                                                        onClick={changePasswordApi}
                                                        disabled={
                                                            !passwordChecked ||
                                                            passwordValidationError?.newPassword ||
                                                            passwordValidationError?.newPasswordConfirm ||
                                                            !("newPassword" in passwordValidationError) ||
                                                            !("newPasswordConfirm" in passwordValidationError)
                                                        }
                                                    />
                                                </ButtonSection>
                                            </form>
                                        </FoldView>
                                    </FoldViewWrap>
                                </SectionContents>
                            </Section>

                            {/* Marketing Agreement Section */}
                            <Section>
                                <SectionTitle>마케팅 정보 수신 동의</SectionTitle>
                                <SectionContents style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Checkbox
                                        size="20px"
                                        checked={
                                            user.is_marketing_agreed === undefined ? false : user.is_marketing_agreed
                                        }
                                        onChange={(e) => changeMarketingAgreedApi(e.target.checked)}
                                    />
                                    <SectionContentsText>수신에 동의합니다.</SectionContentsText>
                                </SectionContents>
                            </Section>

                            <DivideLine />

                            {/* Unregister Section */}
                            <Section>
                                <SectionTitle>회원탈퇴</SectionTitle>
                                <SectionContents>
                                    <Button
                                        onlyText
                                        bgColor={"var(--color-Button2)"}
                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                        width={"120px"}
                                        height={"32px"}
                                        fontColor={"var(--color-White)"}
                                        buttonText={"탈퇴하기"}
                                        onClick={(e) => {
                                            setShowUnregisterSurvey(true);
                                        }}
                                    />
                                </SectionContents>
                            </Section>
                        </ContentsWrap>
                    </BodyWithBreadcrumb>
                </>
            ) : (
                <>
                    <Breadcrumb>
                        <BreadcrumbTitle>
                            <Tooltip message={"뒤로"}>
                                <Button
                                    onlyIcon
                                    bgColor={"var(--color-Button4)"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    fontColor={"var(--color-Black)"}
                                    onClick={() => {
                                        setShowUnregisterSurvey(false);
                                    }}
                                    margin={"0 12px 0 0"}
                                    disabled={window.history.length === 1}
                                >
                                    <Icon name="arrowLeft" size="12px" />
                                </Button>
                            </Tooltip>
                            회원탈퇴
                        </BreadcrumbTitle>
                    </Breadcrumb>
                    <BodyWithBreadcrumb>
                        <SurveyWrap>
                            <SurveyTitle>탈퇴 전 잠시만 설문 응답을 부탁드려요.</SurveyTitle>
                            <SurveyDescription>프렙을 계속해서 개선 중입니다.</SurveyDescription>
                            <SurveyDescription>
                                부족한 부분을 알려주시면 적극적으로 개선에 반영하겠습니다.
                            </SurveyDescription>
                            <SurveyDescription>
                                설문 내용은 익명으로 수집되며 서비스 개선에만 사용됩니다.
                            </SurveyDescription>
                            <SurveyNote>* 기존 가입자의 쿠폰 지급 정책은 유지됩니다.</SurveyNote>
                            {questions.length > 0 && renderSurveyQuestions(questions)}

                            <Button
                                onlyText
                                bgColor={"var(--color-Button2)"}
                                hoverBgColor={"var(--color-ButtonHover2)"}
                                width={"120px"}
                                height={"32px"}
                                fontColor={"var(--color-White)"}
                                buttonText={"탈퇴하기"}
                                onClick={(e) => {
                                    unregisterApi();
                                }}
                                margin={"36px 0 0 0"}
                            />
                        </SurveyWrap>
                    </BodyWithBreadcrumb>
                </>
            )}
        </LoginUserLayout>
    );
}

export default AccountPage;
